import React from "react";

import { sub } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../context/StateContext";
import { usePurchaseOrderContext } from "../../../../../hooks/usePurchaseOrderContext";
import useTimezoneDate from "../../../../../hooks/useTimezoneDate";
import { MilestoneType } from "../../../../../models/milestone";
import { Port } from "../../../../../models/port";
import { PurchaseOrder } from "../../../../../models/purchase.order";
import { OrderDetailsFields } from "../../../../../util/enums";
import { useFormatCurrency } from "../../../../../util/useFormatCurrency";
import { FieldViewProps } from "../../../../Form/FieldView";
import CollapsablePanel from "../../../../Generic/Collapse";
import { getDateValueInTimeZone } from "../../../../Generic/MilestoneDates";
import { SectionView } from "../../../../Generic/SideView/SectionView";
import getOrderOptions from "../../../getOrderOptions";
import { usePurchaseOrderDetails } from "../../../hooks/usePurchaseOrderDetails";
import { CollapseBody, CollapseContainer } from "./styles";

type PurchaseOrderViewDetailsProps = {
  purchaseOrder?: PurchaseOrder | undefined;
  portCodes?: Port[];
};
export const OrderViewDetails: React.FC<PurchaseOrderViewDetailsProps> = (props) => {
  const { purchaseOrder, portCodes } = props;
  const { isFMVersion, isSupplierPortal } = useAppState();
  const { getDateValueUtcTimezone } = useTimezoneDate();
  const { milestoneDates } = usePurchaseOrderContext();

  const dateViewFormat = (dateString: string | undefined) => {
    if (!dateString) return undefined;

    return getDateValueUtcTimezone(dateString, "dd/MM/yyyy");
  };

  const { t: translate } = useTranslation();
  const {
    originPortSelected,
    destinationPortSelected,
    customerSelected,
    loadingCustomers,
    fetchingMilestones,
  } = usePurchaseOrderDetails({ purchaseOrder, portCodes });
  const { orderTypes } = getOrderOptions();

  const { formatCurrencyString } = useFormatCurrency();

  const baseViewFieldProps = (key: keyof PurchaseOrder) => {
    const props: FieldViewProps = {
      label: translate(key),
      value: purchaseOrder?.[key] ? String(purchaseOrder?.[key]) : undefined,
    };

    return props;
  };
  const type = MilestoneType.ESTIMATED;
  const milestoneOptions = milestoneDates.filter(
    (milestoneOption) => milestoneOption.type?.toLowerCase() === type.toLowerCase()
  );

  const milestoneFields = [
    milestoneOptions.map((milestoneOption) => {
      const milestoneValue = purchaseOrder?.milestones?.find(
        (milestoneValue) => Number(milestoneValue?.milestoneId) === Number(milestoneOption?.id)
      );

      const keyName = milestoneOption.reference || milestoneOption.title;

      return {
        name: keyName,
        label: milestoneOption.title,
        value: milestoneValue?.dateValue
          ? getDateValueInTimeZone(milestoneValue?.dateValue, "dd/MM/yyyy")
          : undefined,
      };
    }),
  ];

  const generalFields = [
    [
      {
        ...baseViewFieldProps(OrderDetailsFields.REFERENCE),
        label: translate("orderNumber"),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.TYPE),
        value: orderTypes.find((type) => type.value === purchaseOrder?.[OrderDetailsFields.TYPE])
          ?.label,
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.DATE),
        value: dateViewFormat(purchaseOrder?.date),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.ETA),
        label: translate("bookByDate"),
        value: purchaseOrder?.currentEta
          ? getDateValueUtcTimezone(
              sub(new Date(purchaseOrder?.currentEta), { days: 7 }).toISOString(),
              "dd/MM/yyyy"
            )
          : undefined,
      },
    ],
    [
      {
        ...baseViewFieldProps(OrderDetailsFields.ORIGIN_PORT_ID),
        label: translate("originPort"),
        value: originPortSelected?.siteCode,
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.DESTINATION_PORT_ID),
        label: translate("destinationPort"),
        value: destinationPortSelected?.siteCode,
      },

      {
        ...baseViewFieldProps(OrderDetailsFields.SHIP_VIA),
        label: translate("carrier"),
      },
      ...(!isFMVersion
        ? [
            {
              ...baseViewFieldProps(OrderDetailsFields.BE_CODE),
              label: translate("maerskBeCode"),
              value: purchaseOrder?.supplier?.beCode,
            },
          ]
        : []),
    ],
    [
      {
        ...baseViewFieldProps(OrderDetailsFields.PURCHASING_ORGANISATION),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.CONSIGNEE),
        value: customerSelected?.name,
        loading: loadingCustomers,
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.SUPPLIER),
        value: purchaseOrder?.supplier?.name,
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.SERVICE_TYPE),
      },
    ],
    [
      {
        ...baseViewFieldProps(OrderDetailsFields.SHIP_TO),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.CLIENT_CODE),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.REMARK),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.ADDRESS_CODE),
      },
    ],
  ];

  const generalFieldsSecondary = [
    [
      {
        ...baseViewFieldProps(OrderDetailsFields.DIVISION_CODE),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.DIVISION_DESCRIPTION),
        label: translate("brand"),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.DEPARTMENT_CODE),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.DEPARTMENT_DESCRIPTION),
      },
    ],
    [
      ...(!isSupplierPortal
        ? [
            {
              ...baseViewFieldProps(OrderDetailsFields.FOB_PRICE),
              value: formatCurrencyString(purchaseOrder?.[OrderDetailsFields.FOB_PRICE]),
            },
          ]
        : []),
      {
        ...baseViewFieldProps(OrderDetailsFields.INCOTERM),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.MODE),
      },
      {
        ...baseViewFieldProps(OrderDetailsFields.ETA),
        label: translate("shipDate"),
        value: dateViewFormat(purchaseOrder?.currentEta),
      },
    ],
    [
      {
        ...baseViewFieldProps(OrderDetailsFields.DELIVERY),
        value: purchaseOrder?.delivery?.longAddress,
      },
    ],
  ];

  const supplierFields = [
    [
      {
        value: purchaseOrder?.supplier?.code,
        label: translate("supplierCode"),
      },
      {
        value: purchaseOrder?.supplier?.name,
        label: translate("supplierName"),
      },
      {
        value: purchaseOrder?.supplier?.email,
        label: translate("email"),
      },
    ],
    [
      {
        value: purchaseOrder?.supplier?.location?.addressLine1,
        label: translate("address1"),
      },
      {
        value: purchaseOrder?.supplier?.location?.addressLine2,
        label: translate("address2"),
      },
      {
        value: purchaseOrder?.supplier?.location?.number,
        label: translate("streetNumber"),
      },
    ],
    [
      {
        value: purchaseOrder?.supplier?.location?.suburb,
        label: translate("suburbTown"),
      },
      {
        value: purchaseOrder?.supplier?.location?.state,
        label: translate("state"),
      },
      {
        value: purchaseOrder?.supplier?.location?.country,
        label: translate("country"),
      },
    ],
    [
      {
        value: purchaseOrder?.supplier?.location?.postalcode,
        label: translate("postalCode"),
      },
    ],
  ];

  return (
    <>
      <SectionView fields={generalFields} />
      <SectionView fields={generalFieldsSecondary} />
      <CollapseContainer container item>
        <CollapsablePanel title={translate("supplierDetails")} filledBackground>
          <CollapseBody>
            <SectionView fields={supplierFields} md={4} />
          </CollapseBody>
        </CollapsablePanel>
      </CollapseContainer>
      <SectionView
        fields={milestoneFields}
        title={translate("estimatedDates")}
        loading={fetchingMilestones}
      />
    </>
  );
};
